import { useEffect, useState } from "react";

import {
    scrollElementIntoViewOnKeyboardVisible,
    adjustContentIntoView,
    restoreContentIntoView,
    getContentNode,
    getDataKeyboardScrollNode,
} from "../utils";

declare global {
    interface Window {
        keyboardStateChanged: (visible: boolean, keyboardHeight: number) => void;
    }
}

const BOTTOM_OFFSET = 24;

const useKeyboardState = (): { isKeyboardOpen: boolean } => {
    const [isKeyboardOpen, setKeyboardOpen] = useState(false);

    useEffect(() => {
        let timer: number | null = null;
        let container: HTMLElement | null = null;
        let canAdjustHeight = false;
        const oldFn = window.keyboardStateChanged;

        const clearTimer = (): void => {
            if (timer) window.cancelAnimationFrame(timer);
        };

        const reset = (containerVal: HTMLElement | null): void => {
            document.documentElement.style.removeProperty("overflow-y");
            document.body.style.transform = `translateY(0)`;
            document.documentElement.style.removeProperty("touch-action");
            setKeyboardOpen(false);
            clearTimer();
            restoreContentIntoView(containerVal);
            canAdjustHeight = true;
        };

        if (typeof oldFn === "function") {
            oldFn(false, 0);
        }

        window.keyboardStateChanged = (visible: boolean, keyboardHeight: number): void => {
            /**
             * if keyboard is visible,
             *  => scroll the focused element into view if required
             *  => change content height w.r.t to keyboard
             * else
             *  => restore content height to full view avaialble
             */
            if (visible) {
                setKeyboardOpen(true);
                const focusedElement = document.activeElement;

                if (!focusedElement) {
                    return;
                }

                const parentElement = getDataKeyboardScrollNode(focusedElement);

                if (keyboardHeight > 0) {
                    // used for suggestive typing component
                    document.documentElement.style.setProperty("--ios-keyboard-height", `${keyboardHeight}`);
                    document.documentElement.style.setProperty("overflow-y", "hidden");
                    document.documentElement.style.setProperty("touch-action", "none");
                }
                const rect = focusedElement.getBoundingClientRect();

                // take existing container instance; when switching from one focused to another focused
                const newContainer = getContentNode(focusedElement, keyboardHeight);
                if (newContainer !== container) {
                    reset(container);
                }

                container = newContainer;

                clearTimer();
                timer = window.requestAnimationFrame(() => {
                    if (parentElement) {
                        document.body.style.transform = `translateY(-${keyboardHeight}px)`;
                    }
                    if (canAdjustHeight) {
                        adjustContentIntoView(container, keyboardHeight);
                    }
                    canAdjustHeight = false;
                    scrollElementIntoViewOnKeyboardVisible({
                        container,
                        rect,
                        bottomOffset: BOTTOM_OFFSET,
                    });
                });
            } else {
                setKeyboardOpen(false);
                reset(container);
            }
        };

        return (): void => {
            clearTimer();
            window.keyboardStateChanged = oldFn;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { isKeyboardOpen };
};

export { useKeyboardState };
